import { EmptyState, ErrorState } from "~/components/screens/StateScreen"
import { TooltipButton } from "~/components/ui/button"
import { SuggestionRow } from "~/components/SuggestionRow"
import { SettingsIcon } from "lucide-react"
import { usePostHog } from "posthog-js/react"
import { useEffect, useMemo, useState } from "react"
import { Link } from "~/components/Link"
import { AppHeader } from "~/components/AppHeader"
import { StatusIcon } from "~/components/icons/status"
import { captureFeatureFlagView } from "~/components/providers/Posthog"
import { ListSection } from "~/components/SectionHeader"
import { LoadingState } from "~/components/screens/StateScreen"
import { EntityIdentity } from "~/components/EntityIdentity"

import { ScrollArea } from "~/components/ui/scroll-area"
import { Search } from "~/components/ui/search"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "~/components/ui/select"
import { TooltipProvider } from "~/components/ui/tooltip"
import { useTeam } from "~/hooks/useTeam"
import { IssueStatus } from "~/lib/enums"
import { enumFormat } from "~/lib/utils"
import { api } from "~/trpc/react"

const suggestionSortOptions = {
	newest: "Newest first",
	oldest: "Oldest first",
	upvotes: "Most upvotes"
} as const

type SuggestionSort = keyof typeof suggestionSortOptions

export default function TeamSuggestionsPage() {
	const { team, isLoading: isTeamLoading } = useTeam()
	const [sort, setSort] = useState<SuggestionSort>("newest")
	const [search, setSearch] = useState("")
	const [query, setQuery] = useState("")
	const posthog = usePostHog()
	const utils = api.useUtils()
	const {
		data: suggestions,
		isLoading,
		error
	} = api.suggestions.list.useQuery(
		{ teamId: team?.id ?? "", query },
		{ enabled: !!team?.id }
	)

	useEffect(() => {
		captureFeatureFlagView("suggestions")
	}, [])

	useEffect(() => {
		const timeout = window.setTimeout(() => setQuery(search.trim()), 250)
		return () => window.clearTimeout(timeout)
	}, [search])

	useEffect(() => {
		if (!query || !team?.id) return
		posthog.capture("suggestions_search_performed", {
			teamId: team.id,
			queryLength: query.length
		})
	}, [posthog, query, team?.id])

	api.suggestions.onUpdate.useSubscription(
		{ teamId: team?.id ?? "" },
		{
			enabled: !!team?.id,
			onData: () => {
				void utils.suggestions.list.invalidate({ teamId: team?.id ?? "" })
				void utils.user.getTeams.invalidate()
			}
		}
	)

	const groups = useMemo(
		() =>
			Object.values(IssueStatus).map((status) => ({
				id: status,
				name: enumFormat(status),
				items: (suggestions ?? [])
					.flatMap((suggestion) =>
						suggestion?.displayStatus === status ? [suggestion] : []
					)
					.sort((a, b) => {
						if (sort === "upvotes") {
							return (
								b.voteCount - a.voteCount ||
								b.createdAt.getTime() - a.createdAt.getTime()
							)
						}
						if (sort === "oldest") {
							return a.createdAt.getTime() - b.createdAt.getTime()
						}
						return b.createdAt.getTime() - a.createdAt.getTime()
					})
			})),
		[suggestions, sort]
	)

	if (isTeamLoading || !team) return <LoadingState />

	return (
		<main className="flex h-full flex-col">
			<TooltipProvider>
				<AppHeader
					contentClassName="flex items-center gap-1.5"
					actions={
						<div className="flex items-center gap-2">
							<Search
								value={search}
								onChange={(value) => setSearch(value.slice(0, 120))}
								placeholder="Search suggestions"
							/>
							<Select
								value={sort}
								onValueChange={(value) => setSort(value as SuggestionSort)}
							>
								<SelectTrigger className="w-[150px]" size="sm">
									<SelectValue aria-label="Sort suggestions" />
								</SelectTrigger>
								<SelectContent>
									{Object.entries(suggestionSortOptions).map(
										([value, label]) => (
											<SelectItem key={value} value={value}>
												{label}
											</SelectItem>
										)
									)}
								</SelectContent>
							</Select>
							<TooltipButton
								asChild
								variant="ghost"
								size="sm"
								tooltip="Suggestion settings"
							>
								<Link to={`/${team.organization.slug}/${team.slug}/settings`}>
									<SettingsIcon className="size-4" />
								</Link>
							</TooltipButton>
						</div>
					}
				>
					<EntityIdentity kind="team" name={team.name} image={team.logo} />
					<span className="text-text-small">/</span>
					<span className="font-medium text-text-main">Suggestions</span>
				</AppHeader>
			</TooltipProvider>
			{isLoading || search.trim() !== query ? (
				<LoadingState label="Loading suggestions" />
			) : error ? (
				<ErrorState
					title="Unable to load suggestions."
					description={error.message}
				/>
			) : suggestions?.length ? (
				<ScrollArea className="h-full">
					<div className="flex w-full flex-col gap-4 px-3 py-4 sm:px-5 lg:px-6">
						{groups
							.filter((group) => group.items.length > 0)
							.map((group) => (
								<ListSection
									key={group.id}
									variant="raised"
									icon={<StatusIcon status={group.id} />}
									title={group.name}
									count={group.items.length}
									itemName="suggestion"
								>
									<div>
										{group.items.map((suggestion) =>
											suggestion ? (
												<SuggestionRow
													key={suggestion.id}
													suggestion={suggestion}
													status={suggestion.displayStatus}
													commentCount={suggestion.comments.length}
													createdAt={suggestion.createdAt}
													to={`/${team.organization.slug}/${team.slug}/suggestions/${suggestion.id}`}
												/>
											) : null
										)}
									</div>
								</ListSection>
							))}
					</div>
				</ScrollArea>
			) : (
				<EmptyState
					title={
						query ? "No suggestions match that search." : "No suggestions yet."
					}
				/>
			)}
		</main>
	)
}
